import { Vec2, World } from "planck-js";
import GameConfig from "../core/data/GameConfig";
import { GameObject } from "../core/GameObject";
import ResizableScene from "../core/ResizableScene";
import { CameraController } from "../game/CameraController";
import { Crate } from "../game/Crate";
import { Dynamite } from "../game/Dynamite";
import { Firecracker } from "../game/Firecracker";
import { GameLayers, LayerName } from "../game/GameLayers";
import { GameUI } from "../game/GameUI";
import { LTGame } from "../game/LTGame";
import MissionTracker from "../game/MissionTracker";
import { Player } from "../game/Player";
import { RecapPopup } from "../game/RecapPopup";
import { ScrollingBG } from "../game/ScrollingBG";
import { Skates } from "../game/Skates";
import SpriteSheetAssist from "../game/SpriteSheetAssist";
import { Trampoline } from "../game/Trampoline";
import { GameCompleteScreen } from "../ui/GameCompleteScreen";
import { PauseScreen } from "../ui/PauseScreen";
import ArrayUtils from "../utils/ArrayUtils";

export type InGameMissionProgress = {
	type: string;
	amt: number;
	goal: number;
	completed: boolean;
}
export class GameScene extends ResizableScene {

	accum: number;

	transitionedIntoScene = false;
	gameContainer: Phaser.GameObjects.Container;
	uiContainer: Phaser.GameObjects.Container;
	cameraController: CameraController;
	ltGame: LTGame;
	objects: GameObject[];
	ui: GameUI;
	missionTracker: MissionTracker;
	paused: boolean;
	scrollingBG: ScrollingBG;
	static spinePaused = false;

	world: World;
	static worldScale = 128;

	layers: GameLayers;
	dtMod: number;
	previousMissionProgress: number[]=[]; //used for char unlock mission meter fill
	currentMissionProgress: InGameMissionProgress[] = []; //track in game mission progress, but don't save until recap

	static ON_OBJECT_ADDED = "onObjectAdded";

	init(data?: { transitioned?: boolean }) {
		this.transitionedIntoScene = data?.transitioned ?? false;
		console.log("data", data);
		console.log("transitioned", this.transitionedIntoScene);
		super.init();
	}
	
	create(): void {
		super.create();
		this.music.play("bedrockTrack");
		this.accum = 0;
		this.dtMod = 1;
		this.paused = false;

		SpriteSheetAssist.createAnim(this, "coinBurst", "coin-sheet", "wb-coin-burst_", 0, 9, 2, 0);
		SpriteSheetAssist.createAnim(this, "coinIdle", "coin-sheet", "wb-coin-idle_", 0, 19, 2);

		this.gameContainer = this.add.container();
        this.uiContainer = this.add.container();
        const uiCamera = this.cameras.add();
        uiCamera.setName("ui");
        this.cameras.main.ignore(this.uiContainer);
        uiCamera.ignore(this.gameContainer);

		this.layers = new GameLayers(this);

		this.objects = [];

		this.cameraController = new CameraController(this);

		this.scrollingBG = new ScrollingBG(this);
		this.cameras.main.on(Phaser.Cameras.Scene2D.Events.PRE_RENDER, () => this.preRender());

		const gameUI = this.ui = new GameUI(this);
		this.uiContainer.add(gameUI);

		const gravity = new Vec2(0, GameConfig.get<number>("baseGravity"));
		this.world = new World(gravity);
		
		this.missionTracker = new MissionTracker(this);

		this.ltGame = new LTGame(this);

		// Debug game complete
		if (GameConfig.get("enableCoinsOnShopBacker") as boolean) this.input.keyboard.on("keydown-P", () => {
			const screen = new GameCompleteScreen(this, () =>{
				const recap = new RecapPopup(this, this.ltGame.getPlayerDistance(), this.ltGame.coins);
				this.uiContainer.add(recap);
				this.addResize(() => {
					this.alignIn.Center(recap, this.viewport);
				});
			}, this.scene);
			this.uiContainer.add(screen);
		});

		this.resize();
	}

	shutdown(): void {
		this.previousMissionProgress = [];
		this.currentMissionProgress = [];
		this.ltGame.shutdown();
		this.ltGame = null;
		this.objects = [];
		super.shutdown();
	}

	updateMusicTrack(index: number): void{
		const musicTracks = ["bedrockTrack", "metroTrack", "scoobyTrack", "gothamTrack", "gameWin"];
		console.log(`selected track: ${musicTracks[index]}`);
		this.music.play(musicTracks[index]);
	}

	update(_t: number, dt: number): void {
		dt /= 1000;
		dt *= this.dtMod;
		// dt *= 0.2;

        if (this.paused) return;
		this.accum += dt;
        const fixedDt = 1 / 60;
        while (this.accum > fixedDt) {
			this.world.step(fixedDt);
            for (const obj of this.objects) obj.physicsUpdate(fixedDt);
            this.world.clearForces();
            // this.cameraController.update(fixedDt);
            this.accum -= fixedDt;
        }
        for (const obj of this.objects) obj.update(dt);
		this.ltGame.update(dt);
		
		if (this.objects.length > 800) console.error("Too many objects!", this.objects.length);
		this.cameraController.update(dt);
		// this.scrollingBG.update();

		this.ui.update(dt);
		this.missionTracker.update(dt);
	}
	
	preRender(): void {
		this.cameraController.preRender();
		this.scrollingBG.update();
		for (const obj of this.objects) obj.preRender();
	}

	addObject<T extends GameObject>(obj: T, layer: LayerName = "default"): T {
		this.layers.addObject(obj, layer);
        this.objects.push(obj);
		obj.once(Phaser.GameObjects.Events.DESTROY, () => this.removeObject(obj));
		this.events.emit(GameScene.ON_OBJECT_ADDED, obj);
        return obj;
    }

    removeObject(obj: GameObject): void {
        if (this.objects) ArrayUtils.remove(this.objects, obj);
	}
	
	pause(): void {
		const pop = new PauseScreen(this);
		this.uiContainer.add(pop);
		this.pausePhaserSystems();
		pop.once(PauseScreen.ON_CLOSED, () => this.resumePhaserSystems());
	}

	gotoSplash(): void {
		this.onExitScene();
		this.scene.start("PreloaderScene", { destinationScene: "SplashScene" });
	}

	gotoShop(): void {
		this.onExitScene();
		this.scene.start("ShopScene");
	}

	restart(): void {
		this.onExitScene();
		this.scene.start("GameScene", {});
	}

	onExitScene(): void {
		this.resumePhaserSystems();
	}

	pausePhaserSystems(): void {
		this.paused = true;
		const spines = (<unknown>this.sys.updateList.getActive().filter(l => l.type == "Spine")) as SpineGameObject[];
		for (const spine of spines) {
			spine.setData("timescale", spine.timeScale);
			spine.timeScale = 0;
		}
		this.physics.world.pause();
		this.time.paused = true;
		this.tweens.pauseAll();
		this.anims.pauseAll();
	}

	tutSlow(): void {
		const slowTweens: Phaser.Tweens.Tween[] = [];
		const tutSpeed = GameConfig.get("slowTutDTMultiplier") as number;
		const tweenDuration = GameConfig.get("slowTutTweenDuration") as number;
		const tweenDelay = GameConfig.get("timeBeforeSlowTut") as number;
		const slowTween = this.tweens.add({
			targets: this,
			dtMod: tutSpeed,
			duration: tweenDuration,
			delay: tweenDelay,
			onComplete: () => {
				this.time.delayedCall(GameConfig.get<number>("slowTutPauseDuration"), () => {
					this.ltGame.player.createTutFinger();
					this.ltGame.player.slamDelay = 0;
				});
			}
		});
		const slowTween2 = this.tweens.add({
			targets: this.ltGame.overlayObj,
			alpha: 1,
			duration: tweenDuration,
			delay: tweenDelay,
			onUpdate: () => {
				const player = this.ltGame.player;
				this.ltGame.overlayObj.setPosition(player.x, player.y);
			}
		});
		const slowTween3 = this.tweens.add({
			targets: this.ui.slamMeter.tut,
			alpha: 1,
			scale: 1,
			duration: tweenDuration,
			delay: tweenDelay,
			ease: Phaser.Math.Easing.Quadratic.Out,
		});
		slowTweens.push(slowTween, slowTween2, slowTween3);
		const spines = (<unknown>this.sys.updateList.getActive().filter(l => l.type == "Spine")) as SpineGameObject[];
		for (const spine of spines) {
			spine.setData("timescale", spine.timeScale);
			const tween = this.tweens.add({
				targets: spine,
				timeScale: tutSpeed,
				duration: tweenDuration - 1,
				delay: tweenDelay,
				onUpdate: () => {
					const spines2 = (<unknown>this.sys.updateList.getActive().filter(l => l.type == "Spine")) as SpineGameObject[];
					for (const spine2 of spines2) {
						if (spine2.timeScale > spine.timeScale) spine2.timeScale = spine.timeScale;
					}
				}
			});
			slowTweens.push(tween);
		}
		this.ltGame.player.slamDelay = 1;
		this.ltGame.player.on(Player.STOP_TUT_SLOW, () => {
			for (const tween of slowTweens) tween.stop();
			this.ltGame.overlayObj.setAlpha(0);
			this.ui.slamMeter.tut.setAlpha(0);
			this.tutResume();
		});
		// this.physics.world.timeScale = this.dtMod;
		// this.time.timeScale =this.dtMod;
		// const tweens = this.tweens.getAllTweens();
		// for (const tween of tweens) tween.timeScale = this.dtMod;
		// this.anims.globalTimeScale = this.dtMod;
	}

	tutResume(): void {
		this.dtMod = 1;
		const spines = (<unknown>this.sys.updateList.getActive().filter(l => l.type == "Spine")) as SpineGameObject[];
		for (const spine of spines) spine.timeScale = spine.getData("timescale") ?? 1;
		// this.physics.world.timeScale = 1;
		// this.time.timeScale = 1;
		// const tweens = this.tweens.getAllTweens();
		// for (const tween of tweens) tween.timeScale = 1;
		// this.anims.globalTimeScale = 1;
	}

	resumePhaserSystems(): void {
		this.paused = false;
		const spines = (<unknown>this.sys.updateList.getActive().filter(l => l.type == "Spine")) as SpineGameObject[];
		for (const spine of spines) spine.timeScale = spine.getData("timescale") ?? 1;
		this.physics.world.resume();
		this.time.paused = false;
		this.tweens.resumeAll();
		this.anims.resumeAll();
	}

	getPowerupList(): GameObject[] {
		const list: GameObject[] = [];
		list.push(...this.objects.filter(t => t instanceof Dynamite && t.grounded));
		list.push(...this.objects.filter(t => t instanceof Crate && t.grounded));
		list.push(...this.objects.filter(t => t instanceof Firecracker && t.grounded));
		list.push(...this.objects.filter(t => t instanceof Skates));
		list.push(...this.objects.filter(t => t instanceof Trampoline));
		return list;
	}
}